// §4.2 Pricing tab — THE NEGOTIATED-PRICE PANEL (Module 09 §2.9, slice
// P3-C-2, D-263): the per-customer override list with its validity and
// approval state, the Propose dialog, and Revoke.
//
// P3-A's Pricing tab carried one sentence where this panel is ("per-customer
// price overrides arrive with the override slice"). The four §5 rows are on
// the wire now, so the sentence goes and the panel takes its place — a stale
// pending is worse than no pending, because the next reader trusts it.
//
// IT IS A LAZY READ, like the effective list beside it: §4.2's < 2 s budget is
// met by the ONE `GET /v1/customers/{id}`, so nothing this panel needs rides
// that request, and a failed read drops to a single line rather than turning
// a customer record into an error card.
//
// THE LIST RENDERS THROUGH THE REGISTER'S `DataTable` — it is tabular data
// (product, price, quantity floor, validity, state), and a hand-rolled list
// would opt it out of the phone card-collapse contract. It takes no wave-1
// props: the endpoint declares no sort, there is no bulk verb on a price, and
// a handful of rows inside a tab needs no sticky header. Revoke's reason form
// is a row-attached EXPANSION under the row it withdraws (the module-04
// precedent): with five prices listed, a form floating below the table is a
// form whose subject the reader has to reconstruct.
//
// §7 REDACTION IS A SENTENCE HERE, NOT A BLANK. `unit_price_minor` and
// `discount_pct` are OMITTED for a reader without `sales.read_value` (D-225:
// omission, never null). The KIND still renders — "fixed price" vs "discount"
// is what the row is, and every role may know that — and where the figure
// would be, the cell says "not shown to your role". An empty cell would read
// as "nothing was negotiated", which is the opposite of the truth.
//
// TWO GATES, TWO SHAPES (D-263, D-040 two layers). Propose rides the CODE
// (`sales.override.write` — ga/fa/sales; the sales role is further narrowed
// to assigned customers server-side). Revoke rides the ROLE (`isAdmin`):
// the same code gates it on the wire, but the service narrows it to
// facility_admin / group_admin and answers a sales user 403 naming the role —
// so offering the control on the code alone would offer every sales user a
// 403. The sales user is told why the control is absent rather than shown a
// dead one (D-088).
//
// THE OUTCOME IS THE SERVER'S. The guard runs AT SUBMIT (§3.4, §7 "auto-routes
// to approval on breach"): the 201 says `active` or `pending_approval`, and a
// pending row carries `review` — the approver tier for everyone, the figures
// only for the roles that may read them. The panel states what it was told,
// with the figures it was given, and says that a pending price is NOT applied
// until it is approved (§3.2 step 2 ignores it).
//
// `expired` IS DERIVED, NOT READ (D-263). No job writes `status = 'expired'`
// in P3: the column stays `active` past `valid_to` until module 11/12's
// scheduler owns the transition in P4. The engine's validity filter is the
// truth (a past-window row never prices), so the chip says "Expired" for an
// `active` row whose `valid_to` is before today, Revoke is withheld on it
// (the service answers 409 OVERRIDE_TERMINAL for a closed window), and the
// filter does not offer a state the wire cannot answer. "Today" is the
// viewer's calendar (`todayIso`, the quote book's validity band idiom): this
// is display tinting, not data selection (D-089), and the server judges the
// window in the tenant's own calendar when a revoke is actually sent.
//
// 422 PRICE_NOT_LISTED IS A FACT ABOUT THE PRICE BOOK, said by name. §3.2
// step 2 only runs after step 1 found a tier list row, so an override for a
// (customer, product) no tier list prices on `valid_from` — a FIXED price as
// much as a discount (D-263) — would sit `active` and never apply. The
// dialog renders the refusal as a sentence naming the code and the remedy
// (list the product first), never in the error register.

import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { Card } from "../../../../components/card";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { Field } from "../../../../components/field";
import { Modal } from "../../../../components/modal";
import { RefLabel } from "../../../../components/ref-label";
import { SegmentedControl, Select } from "../../../../components/select";
import { StatusChip } from "../../../../components/status-chip";
import { RequestError } from "../../../../lib/request";
import { UnauthorizedError } from "../../../../lib/problem";
import { todayIso } from "../../../../lib/business-dates";
import { formatBusinessDate } from "../../../../lib/datetime";
import { foldPagedRows } from "../../../../lib/paged-rows";
import { bookPager, useBookPages, useBookWindow } from "../hooks/book-pages";
import { useUoms } from "../../catalogue/hooks/catalogue-queries";
import { useProductSearch } from "../../production/hooks/production-queries";
import { formatMinor, minorUnitDigits, normalizeTypedDecimal } from "../lib/money";
import { decimalToMinor } from "../lib/price-import";
import {
  FILTERABLE_OVERRIDE_STATUSES,
  OVERRIDE_KINDS,
  effectiveOverrideStatus,
  overrideIsRevocable,
  overridePriceRedacted,
  overrideStatusVariant,
  parseMinQty,
  parsePct,
  proposalOutcome,
  requiredRolesOf,
  validityOrdered,
  type OverrideKind,
  type ProposalOutcome,
} from "../lib/overrides";
import { MutationFailureNote } from "../mutation-failure-note";
import { isAdminLineage, useSalesPermissions } from "../hooks/permissions";
import {
  useCustomerOverrides,
  useProductIndex,
  type Customer,
  type CustomerOverridesQuery,
  type CustomerPriceOverride,
} from "../hooks/sales-queries";
import {
  useCreateOverride,
  useRevokeOverride,
  type CustomerPriceOverrideCreateRequest,
} from "../hooks/quote-queries";

interface Outcome {
  readonly row: CustomerPriceOverride;
  readonly outcome: ProposalOutcome;
}

export function CustomerOverridesPanel({ customer }: { customer: Customer }) {
  const { t, i18n } = useTranslation();
  const perms = useSalesPermissions();
  const holdsCode = perms.has("sales.override.write");
  const canPropose = holdsCode && customer.status === "active";
  // The ROLE, not the code — see the header. Read on the admin LINEAGE, the
  // key `overrides.service.ts` checks (REVOKE_LINEAGES, D-586).
  const canRevoke = holdsCode && isAdminLineage(perms);
  // The derived-`expired` clock — see the header.
  const today = todayIso();

  const [status, setStatus] = useState("");
  const [proposing, setProposing] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | undefined>(undefined);
  const [revokingId, setRevokingId] = useState<string | undefined>(undefined);
  const [revokeReason, setRevokeReason] = useState("");

  const query: CustomerOverridesQuery = useMemo(
    () =>
      status.length > 0 ? { status: status as NonNullable<CustomerOverridesQuery["status"]> } : {},
    [status],
  );
  const queryKey = JSON.stringify(query);

  // One page at a time (D-560): the stack starts again at page 1 whenever
  // the status filter does.
  const pages = useBookPages(queryKey);
  const page = useCustomerOverrides(customer.id, query, pages.cursor);
  // Rows this panel wrote since the page was last read; a fresh read of the
  // page replaces them (the write invalidates the list).
  const [patched, setPatched] = useState<ReadonlyMap<string, CustomerPriceOverride>>(
    () => new Map(),
  );
  const [patchedFor, setPatchedFor] = useState(page.data);
  if (patchedFor !== page.data) {
    setPatchedFor(page.data);
    setPatched(new Map());
  }
  const rowsAccum = useMemo(() => {
    const folded = foldPagedRows(page.data?.items ?? [], [...patched.values()]);
    return query.status === undefined
      ? folded
      : folded.filter((r) => !patched.has(r.id) || r.status === query.status);
  }, [page.data, patched, query.status]);
  const pageView = useBookWindow(pages, {
    items: rowsAccum,
    nextCursor: page.data?.next_cursor,
    turning: page.isPlaceholderData,
    settled: page.data !== undefined && !page.isFetching,
  });

  const productIds = useMemo(
    () => [...new Set(rowsAccum.map((row) => row.product_id))],
    [rowsAccum],
  );
  const products = useProductIndex(productIds);
  const uoms = useUoms();
  const uomCodeOf = (productId: string): string | undefined => {
    const ref = products.byId.get(productId);
    if (ref === undefined) return undefined;
    return uoms.data?.items.find((uom) => uom.id === ref.base_uom_id)?.code;
  };

  const revoke = useRevokeOverride();
  const revokeRoles = revoke.isError ? requiredRolesOf(revoke.error) : undefined;
  /** A role code the server named, in the viewer's language — the §7 role
   * names the area already carries; a code it has no name for renders as
   * itself rather than as a missing key. */
  const roleLabel = (role: string): string =>
    role === "facility_admin" || role === "group_admin"
      ? t(`sales.overrides.roleName.${role}`)
      : role;

  /** A row the server just answered (a revoke, a proposal) replaces its copy
   * on the page shown, or joins it, until the list is read again (the write
   * invalidates it); under a status filter it no longer matches, it leaves. */
  const patchRow = (row: CustomerPriceOverride) => {
    setPatched((prior) => new Map(prior).set(row.id, row));
  };

  const columns: DataTableColumn<CustomerPriceOverride>[] = [
    {
      key: "product",
      header: t("sales.overrides.colProduct"),
      id: true,
      render: (row) => {
        const ref = products.byId.get(row.product_id);
        return (
          <RefLabel
            value={
              ref === undefined
                ? undefined
                : t("sales.priceCheck.productApplied", { sku: ref.sku, name: ref.name })
            }
            id={row.product_id}
            pending={products.isPending}
          />
        );
      },
    },
    {
      key: "value",
      header: t("sales.overrides.colValue"),
      render: (row) => (
        <OverrideValueCell row={row} uom={uomCodeOf(row.product_id)} locale={i18n.language} />
      ),
    },
    {
      key: "min_qty",
      header: t("sales.overrides.colMinQty"),
      numeric: true,
      render: (row) =>
        Number(row.min_qty) === 0 ? (
          <span data-testid="sales-override-min-qty">{t("sales.overrides.anyQty")}</span>
        ) : (
          <span className="num" data-testid="sales-override-min-qty">
            {/* An unresolved unit drops the WORD, never leaves an empty slot:
                a unitless "500.000" on a pricing screen is a number a reader
                can misread, and the price cell in this same row already
                handles the identical absence by omitting its per-unit phrase. */}
            {uomCodeOf(row.product_id) === undefined
              ? t("sales.overrides.fromQtyNoUom", { qty: row.min_qty })
              : t("sales.overrides.fromQty", {
                  qty: row.min_qty,
                  uom: uomCodeOf(row.product_id),
                })}
          </span>
        ),
    },
    {
      key: "validity",
      header: t("sales.overrides.colValidity"),
      render: (row) => (
        <span className="num" data-testid="sales-override-validity">
          {formatBusinessDate(row.valid_from, i18n.language)} –{" "}
          {row.valid_to === null
            ? t("sales.priceLists.openEnded")
            : formatBusinessDate(row.valid_to, i18n.language)}
        </span>
      ),
    },
    {
      key: "status",
      header: t("sales.overrides.colStatus"),
      render: (row) => <OverrideStatusCell row={row} today={today} locale={i18n.language} />,
    },
    {
      key: "reason",
      header: t("sales.overrides.colReason"),
      render: (row) => <span data-testid="sales-override-reason">{row.reason}</span>,
    },
    ...(canRevoke
      ? [
          {
            key: "actions",
            header: t("sales.overrides.colActions"),
            actions: true,
            render: (row: CustomerPriceOverride) =>
              overrideIsRevocable(row, today) ? (
                <Button
                  size="sm"
                  variant="danger"
                  data-testid="sales-override-revoke"
                  disabled={revokingId !== undefined && revokingId !== row.id}
                  onClick={() => {
                    revoke.reset();
                    setRevokeReason("");
                    setRevokingId(row.id);
                  }}
                >
                  {t("sales.overrides.revoke")}
                </Button>
              ) : null,
          } satisfies DataTableColumn<CustomerPriceOverride>,
        ]
      : []),
  ];

  const revokeForm = (row: CustomerPriceOverride) => {
    if (row.id !== revokingId) return undefined;
    return (
      <div className="filter-bar sales-override-revoke" data-testid="sales-override-revoke-form">
        {/* Disclosed BEFORE the press (§5): pinned sent prices stay, drafts
            reprice on their next edit. */}
        <p className="sales-hint">{t("sales.overrides.revokeNote")}</p>
        <Field label={t("sales.overrides.revokeReason")} wide>
          <input
            value={revokeReason}
            data-testid="sales-override-revoke-reason"
            onChange={(e) => {
              setRevokeReason(e.target.value);
            }}
          />
        </Field>
        <Button
          size="sm"
          variant="danger"
          data-testid="sales-override-revoke-confirm"
          disabled={revokeReason.trim().length === 0}
          busy={revoke.isPending}
          busyLabel={t("sales.overrides.revoking")}
          onClick={() => {
            const reason = revokeReason.trim();
            if (reason.length === 0) return;
            revoke.mutate(
              { id: row.id, body: { reason } },
              {
                onSuccess: (updated) => {
                  setRevokingId(undefined);
                  setRevokeReason("");
                  patchRow(updated);
                },
              },
            );
          }}
        >
          {t("sales.overrides.revokeConfirm")}
        </Button>
        <Button
          size="sm"
          data-testid="sales-override-revoke-cancel"
          disabled={revoke.isPending}
          onClick={() => {
            setRevokingId(undefined);
            setRevokeReason("");
            revoke.reset();
          }}
        >
          {t("sales.common.cancel")}
        </Button>
        {revoke.isError ? (
          <MutationFailureNote
            data-testid="sales-mutation-failed"
            text={t("sales.overrides.revokeFailed")}
            error={revoke.error}
          />
        ) : null}
        {revokeRoles !== undefined ? (
          // The 403 that should never arrive (the control is gated on the
          // role) — if a stale role feed lets it through, the refusal NAMES
          // the roles the server put in `required_role`, in the viewer's
          // language, beside the server's own words above.
          <p className="sales-error-note" role="alert" data-testid="sales-override-revoke-denied">
            {t("sales.overrides.revokeDenied", {
              roles: revokeRoles.map((role) => roleLabel(role)).join(", "),
            })}
          </p>
        ) : null}
      </div>
    );
  };

  const readError = page.error !== null && !(page.error instanceof UnauthorizedError);
  const offline = page.fetchStatus === "paused" && rowsAccum.length === 0;

  return (
    <Card
      className="sales-overrides"
      data-testid="sales-overrides"
      title={t("sales.overrides.title")}
      action={
        <div className="filter-bar">
          <Field label={t("sales.overrides.filterStatus")}>
            <Select
              value={status}
              data-testid="sales-overrides-status"
              onChange={(e) => {
                setStatus(e.target.value);
              }}
            >
              <option value="">{t("sales.overrides.filterAll")}</option>
              {FILTERABLE_OVERRIDE_STATUSES.map((value) => (
                <option key={value} value={value}>
                  {t(`sales.overrides.status.${value}`)}
                </option>
              ))}
            </Select>
          </Field>
          {canPropose ? (
            <Button
              variant="primary"
              size="sm"
              data-testid="sales-override-propose"
              onClick={() => {
                setOutcome(undefined);
                setProposing(true);
              }}
            >
              {t("sales.overrides.propose")}
            </Button>
          ) : null}
        </div>
      }
    >
      <p className="sales-hint">{t("sales.overrides.intro")}</p>
      {holdsCode && !isAdminLineage(perms) ? (
        // The sales user holds the code and may NOT revoke: said, not hidden.
        <p className="sales-hint" data-testid="sales-override-revoke-role">
          {t("sales.overrides.revokeRoleNote")}
        </p>
      ) : null}

      {outcome !== undefined ? (
        <p
          className={outcome.outcome.kind === "active" ? "sales-applied" : "sales-hint"}
          role="status"
          data-testid="sales-override-outcome"
          data-status={outcome.row.status}
        >
          <OutcomeText outcome={outcome.outcome} currency={outcome.row.currency} />
        </p>
      ) : null}

      {offline ? (
        <p className="sales-hint" data-testid="sales-overrides-offline">
          {t("sales.offline.body")}
        </p>
      ) : null}

      {readError ? (
        // One line, not an ErrorState: a failed panel must not turn the
        // customer record into an error screen.
        <p className="sales-hint" data-testid="sales-overrides-error">
          {t("sales.overrides.error")}{" "}
          <Button
            size="sm"
            onClick={() => {
              void page.refetch();
            }}
          >
            {t("sales.common.retry")}
          </Button>
        </p>
      ) : null}

      {!offline && !readError ? (
        <DataTable
          data-testid="sales-overrides-table"
          columns={columns}
          rows={rowsAccum}
          rowKey={(row) => row.id}
          rowTestId="sales-override-row"
          pager={bookPager(t, i18n.language, pageView, {
            turning: page.isPlaceholderData,
            onNext: () => {
              const next = page.data?.next_cursor;
              if (next === null || next === undefined) return;
              // The next page starts after the rows the SERVER served here;
              // rows this panel wrote or filtered out since the read would
              // shift every later row number.
              pages.next(next, page.data?.items.length ?? 0);
            },
            onPrev: pages.prev,
          })}
          // `data-id` so a walkthrough addresses ONE row by the id the 201
          // gave it, rather than by kind + reason text. `data-status` is what
          // the glass SAYS (derived — see the header); `data-wire-status` is
          // what the column holds, so a walkthrough can assert both halves of
          // D-263 on one row.
          rowProps={(row) => ({
            "data-id": row.id,
            "data-status": effectiveOverrideStatus(row, today),
            "data-wire-status": row.status,
            "data-kind": row.override_kind,
          })}
          loading={page.isPending && rowsAccum.length === 0}
          loadingLabel={t("sales.common.loading")}
          caption={t("sales.overrides.tableCaption")}
          empty={
            <p className="sales-hint" data-testid="sales-overrides-empty">
              {status.length > 0 ? t("sales.overrides.emptyFiltered") : t("sales.overrides.empty")}
            </p>
          }
          {...(canRevoke
            ? { expansion: revokeForm, expansionLabel: () => t("sales.overrides.revoke") }
            : {})}
        />
      ) : null}

      {proposing ? (
        <ProposeOverrideDialog
          customer={customer}
          onClose={() => {
            setProposing(false);
          }}
          onCreated={(row) => {
            const result = proposalOutcome(row);
            if (result !== undefined) setOutcome({ row, outcome: result });
            patchRow(row);
          }}
        />
      ) : null}
    </Card>
  );
}

/** The kind, then the figure — or, for a reader the server gave no figure
 * to, the kind and the SENTENCE that says why (§7, D-225). */
function OverrideValueCell({
  row,
  uom,
  locale,
}: {
  row: CustomerPriceOverride;
  uom: string | undefined;
  locale: string;
}) {
  const { t } = useTranslation();
  const kind = t(`sales.overrides.kind.${row.override_kind}`);
  if (overridePriceRedacted(row)) {
    return (
      <span className="sales-override-value">
        <span>{kind}</span>
        <StatusChip variant="neutral" dot={false} data-testid="sales-override-redacted">
          {t("sales.overrides.redacted")}
        </StatusChip>
      </span>
    );
  }
  if (row.override_kind === "fixed_price" && row.unit_price_minor != null) {
    const price = formatMinor(row.unit_price_minor, row.currency, locale);
    return (
      <span className="sales-override-value">
        <span>{kind}</span>
        <span className="num" data-testid="sales-override-price">
          {uom === undefined ? price : t("sales.overrides.perUnit", { price, uom })}
        </span>
      </span>
    );
  }
  if (row.override_kind === "discount_pct" && row.discount_pct != null) {
    return (
      <span className="sales-override-value">
        <span>{kind}</span>
        <span className="num" data-testid="sales-override-price">
          {t("sales.overrides.discountValue", { pct: row.discount_pct })}
        </span>
      </span>
    );
  }
  // A kind whose own number is null is a row the CHECK forbids; the kind
  // alone is the honest rendering rather than a figure invented for it.
  return <span>{kind}</span>;
}

/** The state chip — the DERIVED state (an `active` row past its `valid_to`
 * reads "Expired", D-263) — and for a pending row the `review` evidence: the
 * approver tier for everyone, the margin-vs-guard and the per-unit shortfall
 * only for the roles the server gave them to (§7 omission — absent keys, no
 * line). */
function OverrideStatusCell({
  row,
  today,
  locale,
}: {
  row: CustomerPriceOverride;
  today: string;
  locale: string;
}) {
  const { t } = useTranslation();
  const review = row.review;
  const status = effectiveOverrideStatus(row, today);
  return (
    <span className="sales-override-status">
      <StatusChip
        variant={overrideStatusVariant(status)}
        data-testid="sales-override-status"
        data-status={status}
        data-wire-status={row.status}
      >
        {t(`sales.overrides.status.${status}`)}
      </StatusChip>
      {row.status === "pending_approval" && review !== undefined ? (
        <span className="sales-override-review" data-testid="sales-override-review">
          <span>
            {t("sales.overrides.needs", {
              approver: t(`sales.overrides.approver.${review.required_approver}`),
            })}
          </span>
          {review.margin_pct !== undefined && review.guard_pct !== undefined ? (
            <span className="num" data-testid="sales-override-review-margin">
              {t("sales.reviews.lineBreach", {
                margin: review.margin_pct,
                guard: review.guard_pct,
              })}
            </span>
          ) : null}
          {review.delta_minor !== undefined ? (
            <span className="num" data-testid="sales-override-review-delta">
              {t("sales.overrides.reviewDelta", {
                amount: formatMinor(review.delta_minor, row.currency, locale),
              })}
            </span>
          ) : null}
        </span>
      ) : null}
    </span>
  );
}

/** The one sentence a proposal's 201 turns into. */
function OutcomeText({ outcome, currency }: { outcome: ProposalOutcome; currency: string }) {
  const { t, i18n } = useTranslation();
  if (outcome.kind === "active") return <>{t("sales.overrides.outcomeActive")}</>;
  const approver = t(`sales.overrides.approver.${outcome.approver}`);
  const amount =
    outcome.delta_minor !== undefined
      ? formatMinor(outcome.delta_minor, currency, i18n.language)
      : undefined;
  if (amount !== undefined && outcome.margin_pct !== undefined && outcome.guard_pct !== undefined) {
    return (
      <>
        {t("sales.overrides.outcomePendingBreach", {
          approver,
          amount,
          margin: outcome.margin_pct,
          guard: outcome.guard_pct,
        })}
      </>
    );
  }
  if (amount !== undefined) {
    return <>{t("sales.overrides.outcomePendingDelta", { approver, amount })}</>;
  }
  if (outcome.margin_pct !== undefined && outcome.guard_pct !== undefined) {
    return (
      <>
        {t("sales.overrides.outcomePendingMargin", {
          approver,
          margin: outcome.margin_pct,
          guard: outcome.guard_pct,
        })}
      </>
    );
  }
  return <>{t("sales.overrides.outcomePending", { approver })}</>;
}

// ---------------------------------------------------------------------------
// The Propose dialog (§5 POST /v1/customer-price-overrides)
// ---------------------------------------------------------------------------

function ProposeOverrideDialog({
  customer,
  onClose,
  onCreated,
}: {
  customer: Customer;
  onClose: () => void;
  onCreated: (row: CustomerPriceOverride) => void;
}) {
  const { t, i18n } = useTranslation();
  const [skuText, setSkuText] = useState("");
  const [kind, setKind] = useState<OverrideKind>("fixed_price");
  const [valueText, setValueText] = useState("");
  const [minQtyText, setMinQtyText] = useState("");
  const [validFrom, setValidFrom] = useState(() => todayIso());
  const [validTo, setValidTo] = useState("");
  const [reason, setReason] = useState("");

  // Product by EXACT SKU — the price-check idiom: a substring that proposed
  // a price for the wrong product would negotiate the wrong article.
  const search = useProductSearch(skuText.trim());
  const match = useMemo(() => {
    const needle = skuText.trim().toLowerCase();
    if (needle.length === 0) return undefined;
    return (search.data?.items ?? []).find((p) => p.sku.toLowerCase() === needle);
  }, [search.data, skuText]);
  const uoms = useUoms();
  const baseUom =
    match === undefined
      ? undefined
      : uoms.data?.items.find((uom) => uom.id === match.base_uom_id)?.code;

  // The customer's currency, at its own exponent — the only currency a §2.9
  // row can carry (the engine resolves currency-matched overrides only,
  // §9.13), so it is a FACT on the form, never an input.
  const currency = customer.default_currency;
  const digits = minorUnitDigits(currency, i18n.language);
  const priceMinor =
    kind === "fixed_price"
      ? decimalToMinor(normalizeTypedDecimal(valueText, i18n.language), digits)
      : undefined;
  // THE SAME KEYBOARD, TWO RADIO OPTIONS APART (D-274). "Fixed price" learned
  // the locale above and "Discount %" had not, so in ONE dialog `45 000,00` was
  // accepted and `12,5` refused — an adversarial review caught it. A percent is
  // a typed decimal like any other; `parsePct`'s own 0–100 grammar is unchanged
  // and still the thing that decides.
  const pct =
    kind === "discount_pct" ? parsePct(normalizeTypedDecimal(valueText, i18n.language)) : undefined;
  const valueValid = kind === "fixed_price" ? priceMinor !== undefined : pct !== undefined;
  const minQtyEmpty = minQtyText.trim().length === 0;
  const minQty = minQtyEmpty
    ? undefined
    : parseMinQty(normalizeTypedDecimal(minQtyText, i18n.language));
  const minQtyValid = minQtyEmpty || minQty !== undefined;
  const ordered = validityOrdered(validFrom, validTo);
  const canSubmit =
    match !== undefined &&
    valueValid &&
    minQtyValid &&
    ordered &&
    validFrom.length > 0 &&
    reason.trim().length > 0;

  const create = useCreateOverride();
  // §9.13's shape, D-263's rule: an override — fixed price OR discount — for
  // a product no tier list prices for this customer on `valid_from` is a
  // FACT about the price book, not a failure of this dialog. It is named by
  // its code (see the header) and cleared when the product or the start date
  // changes, because it was a fact about THAT pair.
  const notListed =
    create.error instanceof RequestError && create.error.code === "PRICE_NOT_LISTED";

  const submit = () => {
    if (match === undefined || !canSubmit) return;
    const common = {
      customer_id: customer.id,
      product_id: match.id,
      valid_from: validFrom,
      ...(validTo.length > 0 ? { valid_to: validTo } : {}),
      ...(minQty !== undefined ? { min_qty: minQty } : {}),
      reason: reason.trim(),
    };
    const body: CustomerPriceOverrideCreateRequest =
      kind === "fixed_price"
        ? { override_kind: "fixed_price", unit_price_minor: priceMinor ?? 0, ...common }
        : { override_kind: "discount_pct", discount_pct: pct ?? "0.00", ...common };
    create.mutate(body, {
      onSuccess: (row) => {
        onCreated(row);
        onClose();
      },
    });
  };

  return (
    <Modal
      heading={t("sales.overrides.proposeTitle")}
      label={t("sales.overrides.proposeTitle")}
      closeLabel={t("sales.common.close")}
      onClose={onClose}
      className="sales-modal"
      testId="sales-override-propose-modal"
      pending={create.isPending}
    >
      <div className="sales-form">
        <Field label={t("sales.priceCheck.product")} wide>
          <input
            value={skuText}
            placeholder={t("sales.priceCheck.productHint")}
            data-testid="sales-override-sku"
            onChange={(e) => {
              setSkuText(e.target.value);
              if (notListed) create.reset();
            }}
          />
          {skuText.trim().length > 0 ? (
            match !== undefined ? (
              <span className="sales-applied" data-testid="sales-override-product-match">
                {t("sales.priceCheck.productApplied", { sku: match.sku, name: match.name })}
              </span>
            ) : (
              <span className="sales-applied" data-testid="sales-override-product-pending">
                {t("sales.priceCheck.productNoMatch")}
              </span>
            )
          ) : null}
        </Field>

        <Field label={t("sales.overrides.kindLabel")} data-testid="sales-override-kind-field">
          <SegmentedControl
            label={t("sales.overrides.kindLabel")}
            data-testid="sales-override-kind"
            value={kind}
            onChange={(next) => {
              setKind(next);
              setValueText("");
              create.reset();
            }}
            options={OVERRIDE_KINDS.map((value) => ({
              value,
              label: t(`sales.overrides.kind.${value}`),
            }))}
          />
        </Field>

        {kind === "fixed_price" ? (
          <Field label={t("sales.overrides.value")}>
            <input
              inputMode="decimal"
              value={valueText}
              data-testid="sales-override-value"
              onChange={(e) => {
                setValueText(e.target.value);
              }}
            />
            <span className="sales-applied" data-testid="sales-override-currency">
              {baseUom === undefined
                ? t("sales.overrides.currencyFact", { currency })
                : t("sales.overrides.currencyFactUom", { currency, uom: baseUom })}
            </span>
          </Field>
        ) : (
          <Field label={t("sales.overrides.pct")}>
            <input
              inputMode="decimal"
              value={valueText}
              data-testid="sales-override-value"
              onChange={(e) => {
                setValueText(e.target.value);
              }}
            />
            <span className="sales-applied">{t("sales.overrides.pctHint")}</span>
          </Field>
        )}

        <Field label={t("sales.overrides.minQty")}>
          <input
            inputMode="decimal"
            value={minQtyText}
            data-testid="sales-override-min-qty-input"
            onChange={(e) => {
              setMinQtyText(e.target.value);
            }}
          />
          <span className="sales-applied">
            {baseUom === undefined
              ? t("sales.overrides.minQtyHint")
              : t("sales.overrides.minQtyHintUom", { uom: baseUom })}
          </span>
        </Field>

        <Field label={t("sales.overrides.validFrom")}>
          <input
            type="date"
            value={validFrom}
            data-testid="sales-override-valid-from"
            onChange={(e) => {
              setValidFrom(e.target.value);
              if (notListed) create.reset();
            }}
          />
        </Field>
        <Field label={t("sales.overrides.validTo")}>
          <input
            type="date"
            value={validTo}
            min={validFrom}
            data-testid="sales-override-valid-to"
            onChange={(e) => {
              setValidTo(e.target.value);
            }}
          />
          <span className="sales-applied">{t("sales.overrides.validToHint")}</span>
        </Field>
        {!ordered ? (
          // §2.9's validity CHECK, said beside the field before the round trip.
          <p className="sales-error-note" role="alert" data-testid="sales-override-validity-order">
            {t("sales.overrides.validityOrder")}
          </p>
        ) : null}

        <Field label={t("sales.overrides.reason")} wide>
          <textarea
            value={reason}
            rows={3}
            data-testid="sales-override-reason-input"
            onChange={(e) => {
              setReason(e.target.value);
            }}
          />
          <span className="sales-applied">{t("sales.overrides.reasonHint")}</span>
        </Field>
      </div>

      {notListed ? (
        <p className="sales-hint" role="status" data-testid="sales-override-not-listed">
          {t("sales.overrides.notListed", { code: "PRICE_NOT_LISTED" })}
        </p>
      ) : create.isError ? (
        <MutationFailureNote
          data-testid="sales-mutation-failed"
          text={t("sales.overrides.createFailed")}
          error={create.error}
        />
      ) : null}

      <div className="sales-modal-actions">
        <Button onClick={onClose} disabled={create.isPending}>
          {t("sales.common.cancel")}
        </Button>
        <Button
          variant="primary"
          data-testid="sales-override-submit"
          busy={create.isPending}
          busyLabel={t("sales.overrides.proposing")}
          disabled={!canSubmit}
          onClick={submit}
        >
          {t("sales.overrides.propose")}
        </Button>
      </div>
    </Modal>
  );
}
